// Favicon set generation, fully client-side. One source image is rasterized to
// each target size on a canvas; a multi-resolution favicon.ico is assembled in
// pure JS (PNG-embedded ICO entries). Nothing is uploaded.
import { zipSync } from 'fflate';

export const PNG_SIZES = [16, 32, 48, 180, 192, 512] as const;
const ICO_SIZES = [16, 32, 48];

export interface FaviconOutput {
  /** filename -> PNG bytes / file bytes */
  files: Record<string, Uint8Array>;
  /** object URLs for preview (16/32/180...) keyed by size */
  previews: { size: number; url: string }[];
  manifest: string;
  snippet: string;
}

/** Loads a File (PNG/JPG/SVG/...) into an HTMLImageElement. */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    img.src = url;
  });
}

function renderPng(img: HTMLImageElement, size: number): Promise<Uint8Array> {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('toBlob failed'));
      blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)));
    }, 'image/png');
  });
}

function u16(n: number): number[] {
  return [n & 0xff, (n >> 8) & 0xff];
}
function u32(n: number): number[] {
  return [n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >> 24) & 0xff];
}

/** Builds a multi-resolution .ico that embeds PNG images (Vista+ compatible). */
export function buildIco(entries: { size: number; png: Uint8Array }[]): Uint8Array {
  const count = entries.length;
  const header = [...u16(0), ...u16(1), ...u16(count)]; // reserved, type=1 (icon), count
  let offset = 6 + count * 16;
  const dir: number[] = [];
  for (const { size, png } of entries) {
    dir.push(
      size >= 256 ? 0 : size, // width (0 = 256)
      size >= 256 ? 0 : size, // height
      0, // color palette
      0, // reserved
      ...u16(1), // color planes
      ...u16(32), // bits per pixel
      ...u32(png.length), // size of image data
      ...u32(offset), // offset
    );
    offset += png.length;
  }
  const total = 6 + count * 16 + entries.reduce((n, e) => n + e.png.length, 0);
  const out = new Uint8Array(total);
  out.set(header, 0);
  out.set(dir, 6);
  let pos = 6 + count * 16;
  for (const { png } of entries) {
    out.set(png, pos);
    pos += png.length;
  }
  return out;
}

const MANIFEST = `{
  "name": "App",
  "short_name": "App",
  "icons": [
    { "src": "android-chrome-192x192.png", "sizes": "192x192", "type": "image/png" },
    { "src": "android-chrome-512x512.png", "sizes": "512x512", "type": "image/png" }
  ],
  "theme_color": "#ffffff",
  "background_color": "#ffffff",
  "display": "standalone"
}
`;

const SNIPPET = `<link rel="icon" href="/favicon.ico" sizes="any" />
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
<link rel="apple-touch-icon" href="/apple-touch-icon.png" />
<link rel="manifest" href="/site.webmanifest" />`;

export async function generateFavicons(img: HTMLImageElement): Promise<FaviconOutput> {
  const pngBySize = new Map<number, Uint8Array>();
  for (const size of PNG_SIZES) {
    pngBySize.set(size, await renderPng(img, size));
  }

  const files: Record<string, Uint8Array> = {
    'favicon-16x16.png': pngBySize.get(16)!,
    'favicon-32x32.png': pngBySize.get(32)!,
    'favicon-48x48.png': pngBySize.get(48)!,
    'apple-touch-icon.png': pngBySize.get(180)!,
    'android-chrome-192x192.png': pngBySize.get(192)!,
    'android-chrome-512x512.png': pngBySize.get(512)!,
  };

  files['favicon.ico'] = buildIco(ICO_SIZES.map((size) => ({ size, png: pngBySize.get(size)! })));
  files['site.webmanifest'] = new TextEncoder().encode(MANIFEST);

  const previews = [16, 32, 48, 180].map((size) => ({
    size,
    url: URL.createObjectURL(new Blob([pngBySize.get(size)!.slice()], { type: 'image/png' })),
  }));

  return { files, previews, manifest: MANIFEST, snippet: SNIPPET };
}

/** Bundles the generated files into a ZIP (returns bytes for download). */
export function zipFavicons(files: Record<string, Uint8Array>): Uint8Array {
  return zipSync(files, { level: 6 });
}
